/**
 * 100% stacked horizontal bars — amCharts 5 `ColumnSeries` with `stacked: true`.
 * Each row is normalized to 100 on the value axis; segment widths = share of `rowTotal`.
 * Raw counts are kept as `raw0`… for tooltips (`tarsStackedBarChartHelpers`).
 *
 * Data: table with `headers[0]` = category, `headers[1…]` = stack segment names; each row
 * supplies one numeric cell per segment.
 *
 * Mount flow: static amCharts imports register `window.am5` / `am5xy`; we create a Root on the
 * chart div, build Y category + X value axes, push one series per segment, legend + cursor, then
 * dispose on unmount.
 *
 * Code map (see section banners below):
 * - amCharts helpers — hide default axis titles
 * - Chart shell + axes — padding; Y = row labels; X = 0–100% scale (ticks hidden)
 * - plotStackedHorizontalBarSeries — stack series, bar geometry, % bullets, legend, cursor, animation
 * - mountStackedBarChartFromModel — Root, theme, build, resize, dispose
 * - React component — data model, mount effect, empty state, title + chart div
 */
// @ts-ignore amCharts5 bundled IIFE registers globals
import "../../../libs/amcharts5/index";
// @ts-ignore amCharts5 xy bundle
import "../../../libs/amcharts5/xy";
// @ts-ignore Animated theme registers am5themes_Animated
import "../../../libs/amcharts5/themes/Animated";

import type { MutableRefObject } from "react";
import { useLayoutEffect, useMemo, useRef } from "react";
import {
    TARS_CATEGORY_AXIS_LABEL_MAX_WIDTH_PX,
    TARS_HORIZONTAL_BAR_LABEL_PADDING_RIGHT_PX,
    TARS_HORIZONTAL_BAR_LABEL_TO_BAR_GAP_PX,
} from "./tars-chart-column-style";
import { resolveTarsChartFontSizes, type TarsChartFontSizes } from "./tarsChartFontHelpers";
import {
    applyDynamicCornerRadius,
    rowsToTARSStackedBarChartModel,
    type TARSStackedBarChartModel,
    type TARSStackedBarChartTableData,
} from "./tarsStackedBarChartHelpers";
import { resolveLabelFillHex, type TARSChartConfiguration } from "./tarsBarChartHelpers";
import { attachAm5ExportingToRoot, type Am5ChartExportApi } from "./tarsAm5Exporting";
import { prependRootTitleIfAny } from "./tarsAm5RootTitle";
import { bindAm5ExportRefs, clearAm5ExportRefs } from "./tars-chart-export-refs";
import TARSChartCopyImageToolbar from "./TARSChartCopyImageToolbar";

/* eslint-disable @typescript-eslint/no-explicit-any */

// ---------------------------------------------------------------------------
// amCharts helpers (shared by axis + series setup)
// ---------------------------------------------------------------------------

/** Hides the default axis title strip so labels and plot use the space. */
function hideAxisTitleIfPresent(axis: any): void {
    try {
        const title = axis.get("title");
        if (title?.set) title.set("visible", false);
    } catch {
        /* optional */
    }
}

// ---------------------------------------------------------------------------
// Chart shell + axes (what you see around the bars)
// ---------------------------------------------------------------------------
// - XYChart padding: outer margin inside the div before plot + legend.
// - Y axis: category labels (one row per bar), left of the bars.
// - X axis: numeric scale for bar length; labels hidden — only the bar geometry shows values.

const STACKED_CHART_DEFAULT_CATEGORY_LABEL_HEX = "#1e3a5f";
const TARS_STACKED_BAR_NUMBER_FORMAT = "#.#";

function createStackedHorizontalBarAxes(
    amChartsCore: any,
    amChartsXY: any,
    chartRoot: any,
    categoryLabelFill: string,
    fonts: TarsChartFontSizes
): { xyChart: any; yCategoryAxis: any; xValueAxis: any } {
    /* Vertical stack: optional title (prepend) → plot → legend (sibling, not inside XYChart). */
    chartRoot.container.set("layout", chartRoot.verticalLayout);

    /* Plot only; legend is a separate container child below (see plotStackedHorizontalBarSeries). */
    const xyChart = chartRoot.container.children.push(
        amChartsXY.XYChart.new(chartRoot, {
            panX: false,
            panY: false,
            wheelX: "none",
            wheelY: "none",
            paddingTop: 12,
            paddingLeft: 12,
            paddingRight: TARS_HORIZONTAL_BAR_LABEL_TO_BAR_GAP_PX,
            paddingBottom: 16,
        })
    );
    xyChart.setAll({
        width: amChartsCore.percent(100),
        flex: 1,
    });

    /* Y = categories (stack rows): bar rows, label text, vertical spacing between rows. */
    const yCategoryAxis = xyChart.yAxes.push(
        amChartsXY.CategoryAxis.new(chartRoot, {
            categoryField: "label",
            renderer: amChartsXY.AxisRendererY.new(chartRoot, {
                inversed: true,
                /* Lower = tighter rows (less empty space between horizontal bars). */
                minGridDistance: 2,
            }),
        })
    );

    hideAxisTitleIfPresent(yCategoryAxis);
    yCategoryAxis.get("renderer").labels.template.setAll({
        visible: true,
        fontSize: fonts.axis,
        fontWeight: "600",
        fill: amChartsCore.color(categoryLabelFill),
        textAlign: "right",
        oversizedBehavior: "none",
        maxWidth: TARS_CATEGORY_AXIS_LABEL_MAX_WIDTH_PX,
        paddingRight: TARS_HORIZONTAL_BAR_LABEL_PADDING_RIGHT_PX,
        marginRight: TARS_HORIZONTAL_BAR_LABEL_TO_BAR_GAP_PX,
    });
    yCategoryAxis.get("renderer").grid.template.setAll({ visible: false });

    /* X = 0–100% of row total; each full bar spans the plot width (100% stacked). */
    const xValueAxis = xyChart.xAxes.push(
        amChartsXY.ValueAxis.new(chartRoot, {
            min: 0,
            max: 100,
            strictMinMax: true,
            extraMin: 0,
            extraMax: 0,
            renderer: amChartsXY.AxisRendererX.new(chartRoot, {}),
        })
    );
    hideAxisTitleIfPresent(xValueAxis);
    xValueAxis.get("renderer").labels.template.setAll({ visible: false });
    xValueAxis.get("renderer").grid.template.setAll({ visible: false });

    return { xyChart, yCategoryAxis, xValueAxis };
}

// ---------------------------------------------------------------------------
// Series, bars, labels, legend, interaction (the actual data drawing)
// ---------------------------------------------------------------------------

function plotStackedHorizontalBarSeries(
    amChartsCore: any,
    amChartsXY: any,
    chartRoot: any,
    model: TARSStackedBarChartModel,
    xyChart: any,
    yCategoryAxis: any,
    xValueAxis: any,
    legendLabelFill: string,
    fonts: TarsChartFontSizes,
    preserveRowOrder?: boolean
): void {
    const { chartData, series } = model;
    if (chartData.length === 0 || series.length === 0) return;
    const pctTemplate = (idx: number) => `{pctLabel${idx}}`;
    const rawField = (idx: number) => `raw${idx}`;
    const sortedChartData = preserveRowOrder
        ? [...chartData]
        : [...chartData].sort((a, b) => {
              const getStart = (label: string | number) => {
                  const num = parseInt(String(label).split(" ")[0], 10);
                  return isNaN(num) ? 0 : num;
              };

              return getStart(a.label) - getStart(b.label);
          });
    /* One ColumnSeries per stack segment; values are 0–100 shares; stack sums to 100 per row. */
    series.forEach((def, seriesIndex) => {
        const columnSeries = xyChart.series.push(
            amChartsXY.ColumnSeries.new(chartRoot, {
                name: def.name,
                xAxis: xValueAxis,
                yAxis: yCategoryAxis,
                valueXField: def.valueField,
                categoryYField: "label",
                stacked: true,
            })
        );

        const colTpl = columnSeries.columns.template;

        colTpl.setAll({
            height: amChartsCore.percent(88),
            fill: amChartsCore.color(def.fillHex),
            strokeWidth: 0,
            strokeOpacity: 0,
            tooltipText: `{name}: [bold]${pctTemplate(seriesIndex)}[/] (value: {${rawField(
                seriesIndex
            )}.formatNumber('${TARS_STACKED_BAR_NUMBER_FORMAT}')})`,
        });

        applyDynamicCornerRadius(colTpl, series, seriesIndex);
        /* In-bar % labels (pill background); hidden when slice is too small to avoid overlap. */
        columnSeries.bullets.push(() => {
            const label = amChartsCore.Label.new(chartRoot, {
                text: pctTemplate(seriesIndex),
                fill: amChartsCore.color(0xffffff),
                fontSize: fonts.dataLabel,
                fontWeight: "600",
                centerX: amChartsCore.p50,
                centerY: amChartsCore.p50,
                paddingTop: 4,
                paddingBottom: 4,
                paddingLeft: 8,
                paddingRight: TARS_HORIZONTAL_BAR_LABEL_PADDING_RIGHT_PX,
                populateText: true,
            });
            label.set(
                "background",
                amChartsCore.RoundedRectangle.new(chartRoot, {
                    fill: amChartsCore.color(0x000000),
                    fillOpacity: 0.38,
                    cornerRadius: 9,
                })
            );
            label.adapters.add("visible", (_v: unknown, target: any) => {
                const ctx = target.dataItem?.dataContext as Record<string, unknown> | undefined;
                if (!ctx) return false;
                const total = ctx.rowTotal;
                const raw = ctx[rawField(seriesIndex)];
                if (typeof total !== "number" || total <= 0) return false;
                if (typeof raw !== "number" || !Number.isFinite(raw)) return false;
                return raw / total >= 0.035;
            });
            return amChartsCore.Bullet.new(chartRoot, {
                locationX: 0.5,
                locationY: 0.5,
                sprite: label,
            });
        });

        columnSeries.data.setAll(sortedChartData);
    });

    /* Keeps Y-axis category order/labels in sync with the stacked series data. */
    yCategoryAxis.data.setAll(sortedChartData);

    /* Sibling of XYChart under root — below the plot; centered row(s) of items. */
    const legend = chartRoot.container.children.push(
        amChartsCore.Legend.new(chartRoot, {
            layout: chartRoot.gridLayout,
            maxWidth: amChartsCore.percent(100),
            centerX: amChartsCore.p50,
            x: amChartsCore.p50,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 10,
            paddingRight: 10,
        })
    );

    legend.markers.template.setAll({
        width: 20,
        height: 20,
        cornerRadius: 4,
    });
    legend.labels.template.setAll({
        fontSize: fonts.axis,
        fill: amChartsCore.color(legendLabelFill),
        fontWeight: "500",
    });
    legend.data.setAll(xyChart.series.values);

    /* Cursor: no zoom/pan behavior; optional hover is series default. */
    xyChart.set("cursor", amChartsXY.XYCursor.new(chartRoot, { behavior: "none" }));
    /* Entry animation for each stack series + chart container. */
    const stackedSeries = xyChart.series.values;
    for (let i = 0; i < stackedSeries.length; i++) {
        stackedSeries[i].appear(1000);
    }
    xyChart.appear(1000, 100);
}

// ---------------------------------------------------------------------------
// Browser mount: Root + theme + build + resize + dispose
// ---------------------------------------------------------------------------

function mountStackedBarChartFromModel(
    chartContainerElement: HTMLElement,
    model: TARSStackedBarChartModel,
    chartTitle: string | null,
    configuration?: TARSChartConfiguration,
    preserveRowOrder?: boolean
): { dispose: () => void; exportApi: Am5ChartExportApi } | null {
    if (typeof window === "undefined" || model.chartData.length === 0) return null;

    const amChartsCore = (window as any).am5;
    const amChartsXY = (window as any).am5xy;
    const animatedTheme = (window as any).am5themes_Animated;

    if (!amChartsCore?.Root?.new || !amChartsXY) {
        console.warn("TARSStackedBarChart: amCharts globals not ready");
        return null;
    }

    const chartRoot = amChartsCore.Root.new(chartContainerElement);
    if (animatedTheme) {
        chartRoot.setThemes([animatedTheme.new(chartRoot)]);
    }
    const fonts = resolveTarsChartFontSizes(configuration);
    prependRootTitleIfAny(amChartsCore, chartRoot, chartTitle, fonts.title);

    const categoryLabelFill = resolveLabelFillHex(
        configuration?.labelColors,
        "category",
        STACKED_CHART_DEFAULT_CATEGORY_LABEL_HEX
    );

    try {
        const { xyChart, yCategoryAxis, xValueAxis } = createStackedHorizontalBarAxes(
            amChartsCore,
            amChartsXY,
            chartRoot,
            categoryLabelFill,
            fonts
        );
        plotStackedHorizontalBarSeries(
            amChartsCore,
            amChartsXY,
            chartRoot,
            model,
            xyChart,
            yCategoryAxis,
            xValueAxis,
            categoryLabelFill,
            fonts,
            preserveRowOrder
        );
    } catch (err) {
        console.error("TARSStackedBarChart: failed to build chart", err);
        chartRoot.dispose();
        return null;
    }

    requestAnimationFrame(() => {
        try {
            chartRoot.resize();
        } catch {
            /* optional */
        }
    });

    const exportApi = attachAm5ExportingToRoot(chartRoot);
    return { dispose: () => chartRoot.dispose(), exportApi };
}

// ---------------------------------------------------------------------------
// React wrapper: DOM host, data model, lifecycle, empty state, optional title
// ---------------------------------------------------------------------------

export interface TARSStackedBarChartProps {
    data: TARSStackedBarChartTableData;
    id?: string;
    chartCopyRef?: MutableRefObject<(() => Promise<void>) | null>;
    chartExportRef?: MutableRefObject<(() => Promise<Blob>) | null>;
}

export default function TARSStackedBarChart({
    data,
    id = "tars-stacked-bar-chart",
    chartCopyRef: chartCopyRefProp,
    chartExportRef,
}: TARSStackedBarChartProps) {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const disposeChartRef = useRef<(() => void) | null>(null);
    const internalChartCopyRef = useRef<(() => Promise<void>) | null>(null);
    const copyRef = chartCopyRefProp ?? internalChartCopyRef;
    /* Table → amCharts-friendly model (categories, v0..vn, pctLabel*, rowTotal). */
    const model = useMemo(() => rowsToTARSStackedBarChartModel(data), [data]);
    const chartTitle = data.title?.trim() || "";

    /* Mount amCharts when model exists; tear down Root on change/unmount so no leak. */
    useLayoutEffect(() => {
        const chartContainerElement = chartContainerRef.current;
        disposeChartRef.current?.();
        disposeChartRef.current = null;
        clearAm5ExportRefs(copyRef, chartExportRef);
        if (!chartContainerElement || !model) return undefined;
        const chartMountHandle = mountStackedBarChartFromModel(
            chartContainerElement,
            model,
            chartTitle,
            data.configuration,
            data.preserveRowOrder
        );
        disposeChartRef.current = chartMountHandle ? () => chartMountHandle.dispose() : null;
        bindAm5ExportRefs(chartMountHandle?.exportApi, copyRef, chartExportRef);
        return () => {
            disposeChartRef.current?.();
            disposeChartRef.current = null;
            clearAm5ExportRefs(copyRef, chartExportRef);
        };
    }, [model, chartTitle, data.configuration, data.preserveRowOrder, copyRef]);

    /* Invalid / empty table: no chart; prompt to fix pasted JSON. */
    if (!model) {
        return (
            <div className="flex flex-1 items-center justify-center px-8 py-16 text-[13px] font-medium text-slate-400">
                Stacked bar needs a header row and rows: column 1 = category, further columns = numeric stack
                values.
            </div>
        );
    }

    /* Title (optional) + full-height div that amCharts Root attaches to for drawing. */
    return (
        <div className="flex min-h-0 w-full flex-1 flex-col">
            {!chartCopyRefProp ? (
                <div className="mb-2 flex shrink-0 justify-end">
                    <TARSChartCopyImageToolbar copyRef={internalChartCopyRef} />
                </div>
            ) : null}
            <div
                ref={chartContainerRef}
                id={id}
                className="h-full min-h-0 min-h-[250px] w-full flex-1 basis-0"
                style={{ minHeight: 0, height: "100%" }}
            />
        </div>
    );
}
